/**
 * TaskGuideWidget - Collapsible sidebar widget showing onboarding task progress.
 *
 * Collapsed: "Getting Started — X of Y" + progress bar
 * Expanded: Task list with completion state, navigation, dismiss
 * Auto-hides when all tasks complete/dismissed
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useTaskGuide } from '../hooks/useTaskGuide';

interface TaskGuideWidgetProps {
  /** Whether the sidebar is in expanded display mode */
  isExpanded: boolean;
}

export function TaskGuideWidget({ isExpanded }: TaskGuideWidgetProps) {
  const navigate = useNavigate();
  const taskGuide = useTaskGuide();
  const [expanded, setExpanded] = useState(false);

  // Don't render outside TaskGuideProvider (e.g., admin portal)
  if (!taskGuide) {
    return null;
  }

  const { visibleTasks, progress, loading, dismissTask } = taskGuide;

  // Don't render if loading, no visible tasks, or all visible tasks completed
  if (loading || visibleTasks.length === 0 || progress.completed === progress.total) {
    return null;
  }

  // Collapsed mode for minimal sidebar — just show a dot indicator
  if (!isExpanded) {
    return (
      <div className="px-2 pb-3 flex justify-center">
        <div
          className="w-9 h-9 rounded-lg bg-sky-500/10 dark:bg-sky-500/20 flex items-center justify-center cursor-pointer hover:bg-sky-500/20 dark:hover:bg-sky-500/30 transition-colors"
          title={`Getting Started: ${progress.completed} of ${progress.total}`}
        >
          <span className="text-xs font-semibold text-sky-600 dark:text-sky-400">
            {progress.completed}/{progress.total}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700">
        {/* Collapsed header — always visible */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-3 py-2.5 flex items-center justify-between cursor-pointer select-none
            hover:bg-zinc-100 dark:hover:bg-zinc-700/50 rounded-lg transition-colors
            focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2"
        >
          <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">
            Getting Started
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 dark:text-zinc-500">
              {progress.completed} of {progress.total}
            </span>
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5 text-zinc-400" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
            )}
          </div>
        </button>

        {/* Progress bar */}
        <div className="px-3 pb-2">
          <div className="bg-zinc-200 dark:bg-zinc-700 rounded-full h-1 overflow-hidden">
            <div
              className="bg-sky-500 h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Expanded task list */}
        {expanded && (
          <div className="px-2 pb-2 space-y-1">
            {visibleTasks.map((task) =>
              task.completed ? (
                <div
                  key={task.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md opacity-60 transition-colors"
                >
                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                    <Check className="h-2.5 w-2.5 text-white" />
                  </div>
                  <span className="text-xs flex-1 min-w-0 truncate text-zinc-400 dark:text-zinc-500 line-through">
                    {task.label}
                  </span>
                </div>
              ) : (
                <div key={task.id} className="flex items-center gap-2 rounded-md transition-colors">
                  <button
                    onClick={() => navigate(task.path)}
                    className="flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700/50 transition-colors"
                  >
                    <div className="w-4 h-4 rounded-full border-2 border-zinc-300 dark:border-zinc-600 shrink-0" />
                    <span className="text-xs flex-1 min-w-0 truncate text-left text-zinc-700 dark:text-zinc-300">
                      {task.label}
                    </span>
                  </button>
                  {task.dismissible && (
                    <button
                      onClick={() => void dismissTask(task.id)}
                      className="shrink-0 p-0.5 rounded-sm hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors"
                      title="Dismiss"
                      aria-label={`Dismiss ${task.label}`}
                    >
                      <X className="h-3 w-3 text-zinc-400 dark:text-zinc-500" />
                    </button>
                  )}
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}
